/**
 * Ce qu'un code peut donner à voir, et ce qu'il ne peut pas (§3.5).
 *
 * L'aperçu visuel existe pour une raison précise : la plupart des auteurs ne
 * lisent pas le code qu'ils déploient. Un comparatif ligne à ligne leur apprend
 * qu'il y a « 2 lignes ajoutées » sans leur dire si leur application a changé
 * d'apparence. Rendre les deux versions côte à côte le leur dit.
 *
 * Module volontairement pur — aucun DOM, aucune dépendance React — pour rester
 * testable directement sous Vitest.
 */

/** En-tête de fichier posé par l'API sur le code transformé, pour l'affichage. */
const FILE_HEADER = /^\/\/ ===== (.+) =====$/;

/** Ce que l'aperçu peut afficher, ou pourquoi il ne peut pas. */
export type PreviewSource =
  { previewable: true; html: string } | { previewable: false; reason: string };

/**
 * Découpe le code transformé tel que l'API le met en forme pour la comparaison.
 *
 * Ce format n'est pas un choix arbitraire dont on dépendrait à tort : c'est
 * `formatTransformedCode` qui le produit, précisément pour rendre le diff lisible
 * ligne à ligne. On s'en sert ici pour retrouver `index.html`.
 *
 * @returns les fichiers retrouvés, ou `null` si le code n'a pas cette forme
 */
export function splitFlattenedFiles(code: string): Map<string, string> | null {
  const lines = code.split('\n');
  const files = new Map<string, string>();

  let current: string | null = null;
  let buffer: string[] = [];

  const flush = (): void => {
    if (current !== null) files.set(current, buffer.join('\n').replace(/^\n+|\n+$/g, ''));
  };

  for (const line of lines) {
    const header = FILE_HEADER.exec(line);
    if (header?.[1]) {
      flush();
      current = header[1].trim();
      buffer = [];
      continue;
    }
    if (current !== null) buffer.push(line);
  }
  flush();

  return files.size > 0 ? files : null;
}

/**
 * Reconnaît un document ou un fragment HTML.
 *
 * Les artifacts statifiés par Claude sont souvent des fragments : ni `<html>` ni
 * `<body>`, seulement un `<style>`, du balisage et un `<script>`. Les navigateurs
 * les affichent parfaitement, il n'y a donc aucune raison de les refuser.
 */
function looksLikeHtml(code: string): boolean {
  return /<(!doctype|html|body|head|div|main|style|section|canvas|h1|p|table|svg)[\s>]/i.test(code);
}

/** Reconnaît du JSX ou un module JavaScript, qu'un navigateur ne sait pas exécuter tel quel. */
function looksLikeModule(code: string): boolean {
  return /^\s*(import\s|export\s)/m.test(code);
}

/**
 * Message unique de l'impossibilité d'afficher une application React.
 *
 * C'est la limite assumée de l'aperçu : une application React doit être compilée
 * par Vite avant de s'exécuter, ce qu'un navigateur ne fait pas. La faire compiler
 * dans la page supposerait d'embarquer un compilateur de plusieurs mégaoctets
 * dans le portail, pour un usage de comparaison.
 */
export const REACT_NOT_PREVIEWABLE =
  'L’aperçu visuel ne concerne que les pages autonomes. Cette version est une ' +
  'application React : elle doit être compilée pour s’afficher, ce que le ' +
  'navigateur ne fait pas seul. Le comparatif du code reste disponible.';

/**
 * Détermine ce que l'aperçu peut rendre à partir d'un code, transformé ou collé.
 *
 * @param code contenu à afficher, ou `null`
 */
export function previewSource(code: string | null | undefined): PreviewSource {
  if (!code || code.trim().length === 0) {
    return { previewable: false, reason: 'Aucun code à afficher pour cette version.' };
  }

  // Code transformé, déplié par l'API en une suite de fichiers.
  const files = splitFlattenedFiles(code);
  if (files) {
    const html = files.get('index.html');
    if (html && html.trim().length > 0) return { previewable: true, html };
    return { previewable: false, reason: REACT_NOT_PREVIEWABLE };
  }

  // Code brut : celui que l'auteur a collé, ou une réponse non dépliée.
  if (looksLikeModule(code) && !looksLikeHtml(code)) {
    return { previewable: false, reason: REACT_NOT_PREVIEWABLE };
  }
  if (looksLikeHtml(code)) return { previewable: true, html: code };

  return {
    previewable: false,
    reason:
      'Ce code n’est pas une page HTML : l’aperçu ne sait pas l’afficher. Le ' +
      'comparatif du code reste disponible.',
  };
}
